import { Box, Divider, Paper, Stack, Typography } from "@mui/material";
import React from "react";
import { IndianPriceFormatter } from "../../../../../helpers/formatWithCommas";
import { DiscountField } from "./DiscountField";

interface IQuotationSummaryProps {
  subTotal: number | string;
  lineDiscountAmount: number;
  discount: string;
  discountMode: "percentage" | "amount";
  onDiscountChange: (value: string) => void;
  onDiscountModeChange: (mode: "percentage" | "amount") => void;
  overallDiscountAmount: number;
  totalGst: number;
  totalVat: number;
  grandTotal: number;
  totalPaid: number;
  remaining: number;
}

const HighlightRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#e3f2fd",
      px: 2,
      py: 1,
      borderRadius: 1,
    }}
  >
    <Typography fontWeight={600}>{label}</Typography>
    <Typography fontWeight={600}>{children}</Typography>
  </Box>
);

/** Totals across the stay details and every event's items. */
export const QuotationSummary: React.FC<IQuotationSummaryProps> = ({
  subTotal,
  lineDiscountAmount,
  discount,
  discountMode,
  onDiscountChange,
  onDiscountModeChange,
  overallDiscountAmount,
  totalGst,
  totalVat,
  grandTotal,
  totalPaid,
  remaining,
}) => {
  const roundedGrandTotal = Math.round(grandTotal || 0);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}
    >
      <Stack spacing={1}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={600}>
            Sub Total (Item and Stay details)
          </Typography>
          <Typography fontWeight={600}>
            {IndianPriceFormatter(subTotal)}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={600} color="text.secondary">
            Discounted Amount
          </Typography>
          <Typography fontWeight={700}>
            {IndianPriceFormatter(String(lineDiscountAmount || 0))}
          </Typography>
        </Box>

        <DiscountField
          value={discount}
          mode={discountMode}
          onModeChange={onDiscountModeChange}
          onChange={onDiscountChange}
        />

        <HighlightRow label="Total GST">
          {IndianPriceFormatter(String(totalGst || 0))}
        </HighlightRow>

        {totalVat > 0 && (
          <HighlightRow label="Total VAT">
            {IndianPriceFormatter(String(totalVat))}
          </HighlightRow>
        )}

        {overallDiscountAmount > 0 ? (
          <Box
            sx={{ backgroundColor: "#e3f2fd", px: 2, py: 1.5, borderRadius: 1 }}
          >
            <Stack spacing={0.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Initial Grand Total
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  {IndianPriceFormatter(
                    Math.round(roundedGrandTotal + overallDiscountAmount),
                  )}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" fontWeight={600} color="error.main">
                  Discounted Amount (
                  {discountMode === "percentage" ? `${discount}%` : "Flat"})
                </Typography>
                <Typography variant="body2" fontWeight={600} color="error.main">
                  - {IndianPriceFormatter(Math.round(overallDiscountAmount))}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography fontWeight={600}>Grand Total (Remaining)</Typography>
                <Typography fontWeight={600}>
                  {IndianPriceFormatter(roundedGrandTotal)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ) : (
          <HighlightRow label="Grand Total">
            {IndianPriceFormatter(roundedGrandTotal)}
          </HighlightRow>
        )}

        <HighlightRow label="Total Paid Amount">
          {IndianPriceFormatter(String(totalPaid || 0))}
        </HighlightRow>
        <HighlightRow label="Remaining Amount">
          {IndianPriceFormatter(String(remaining || 0))}
        </HighlightRow>
      </Stack>
    </Paper>
  );
};
